import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PolarEmbedCheckout } from '@polar-sh/checkout/embed';
import PublicNav from '../components/PublicNav';
import PublicFooter from '../components/PublicFooter';
import { ff, mono } from '../styles/fonts';
import { usePageMeta } from '../hooks/usePageMeta';
import { useSchema } from '../hooks/useSchema';
import { useAuth } from '../context/AuthContext';

// ─── Tier definition ──────────────────────────────────────────────────────────
export interface Tier {
  name: 'Hiker' | 'Explorer' | 'Master';
  icon: string;
  description: string;
  blurb: string;
  features: string[];
  highlighted: boolean;
  badge?: string;
  planKey: 'free' | 'plus' | 'pro';
  accentHex: string;
}

const TIERS: Tier[] = [
  {
    name: 'Hiker',
    icon: 'directions_walk',
    planKey: 'free',
    accentHex: '#8B9467',
    description: 'Free forever',
    blurb: 'For light exploration. Perfect for focusing on a single challenging subject.',
    features: ['1 Active Subject', '1 Document per Subject', 'Basic Knowledge Quizzes', 'Community Support'],
    highlighted: false,
  },
  {
    name: 'Explorer',
    icon: 'explore',
    planKey: 'plus',
    accentHex: '#D46B4E',
    description: 'Full academic toolkit',
    blurb: 'Dive deep into any topic with AI-guided visualizations and unlimited subjects.',
    features: ['Unlimited Subjects', 'Up to 5 Documents per Subject', 'Socratic AI Tutoring ("Teach Me")', 'AI Video Generation', 'Custom Visualizations', 'Priority Support'],
    highlighted: true,
    badge: 'MOST POPULAR ROUTE',
  },
  {
    name: 'Master',
    icon: 'account_balance',
    planKey: 'pro',
    accentHex: '#407B65',
    description: 'Priority everything',
    blurb: 'For serious learners who need results fast. Priority generation and everything unlimited.',
    features: ['Everything in Explorer', 'Up to 15 Documents per Subject', 'Unlimited Socratic Tutoring', 'Timed Exam Simulator', 'Priority Video Generation', 'Early Access Features', '1-on-1 Support'],
    highlighted: false,
  },
];

// ─── Comparison table ─────────────────────────────────────────────────────────
type ComparisonRow = {
  feature: string;
  free: string | boolean;
  plus: string | boolean;
  pro: string | boolean;
};

const comparisonRows: ComparisonRow[] = [
  { feature: 'Active Subjects',        free: '1',         plus: 'Unlimited', pro: 'Unlimited' },
  { feature: 'Documents per Subject',  free: '1',         plus: '5',         pro: '15' },
  { feature: 'Full-Subject Notes', free: true,        plus: true,        pro: true        },
  { feature: 'Flashcards',         free: true,        plus: true,        pro: true        },
  { feature: 'Unlimited quizzes',      free: true,        plus: true,        pro: true        },
  { feature: 'Socratic AI Tutoring',   free: false,       plus: true,        pro: true        },
  { feature: 'Timed Exam Simulator',   free: false,       plus: true,        pro: true        },
  { feature: 'AI Video Lessons',   free: false,       plus: true,        pro: true        },
  { feature: 'Generation Speed',   free: 'Standard',  plus: 'Priority',  pro: 'Priority'  },
  { feature: 'Support Level',      free: 'Community', plus: 'Priority',  pro: '1-on-1'    },
  { feature: 'Beta Access',        free: false,       plus: false,        pro: true        },
];

function CellValue({ value, isExplorer }: { value: string | boolean; isExplorer?: boolean }) {
  if (value === true) {
    return (
      <span className={`material-symbols-outlined text-[18px] ${isExplorer ? 'text-primary-container' : 'text-on-surface'}`} aria-hidden="true">
        check
      </span>
    );
  }
  if (value === false) {
    return <span className="text-on-surface-variant/30 text-lg">—</span>;
  }
  return <span className={isExplorer ? 'text-primary-container font-semibold' : ''}>{value as string}</span>;
}

// ─── Price fetcher hook ───────────────────────────────────────────────────────
type PriceData = { display: string; amount: number; currency: string } | null;
type PricesResponse = { explorer: PriceData; master: PriceData };

function usePolarPrices(apiUrl: string) {
  const [prices, setPrices]   = useState<PricesResponse>({ explorer: null, master: null });
  const [loading, setLoading] = useState(true);
  const fetchedRef            = useRef(false);

  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    fetch(`${apiUrl}/api/billing/prices`)
      .then(r => r.ok ? r.json() : null)
      .then(json => {
        if (json) setPrices(json);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [apiUrl]);

  return { prices, loading };
}

// ─── Main component ───────────────────────────────────────────────────────────
type CheckoutTierId = 'plus' | 'pro';
type CheckoutState  = 'idle' | 'loading' | 'overlay_open' | 'error';

export function PricingContent({ showCompare = true, isLandingPage = false }: { showCompare?: boolean; isLandingPage?: boolean }) {
  const {
    session,
    plan: currentPlan,
    loading: authLoading,
    refreshPlan,
    earlyAccess,
    earlyAccessDiscountPercent,
    earlyAccessChoice,
    earlyAccessWindowOpen,
  } = useAuth();
  const navigate = useNavigate();

  const apiUrl = import.meta.env.VITE_API_URL ?? '';
  const { prices, loading: pricesLoading } = usePolarPrices(apiUrl);

  const [checkoutState, setCheckoutState] = useState<Record<CheckoutTierId, CheckoutState>>({
    plus: 'idle',
    pro:  'idle',
  });
  const [errorMsg, setErrorMsg]             = useState('');
  const [claimingEarlyAccess, setClaimingEarlyAccess] = useState(false);
  const [choiceLoading, setChoiceLoading]   = useState<'paid_discount' | 'free' | null>(null);
  const [publicWindowOpen, setPublicWindowOpen] = useState(false);

  // Active Polar checkout instance — keep a ref so we can close it if needed
  const activeCheckoutRef = useRef<{ close(): void } | null>(null);

  useEffect(() => {
    fetch(`${apiUrl}/api/billing/early-access-status`)
      .then(r => (r.ok ? r.json() : null))
      .then(json => setPublicWindowOpen(json?.window_open === true))
      .catch(() => setPublicWindowOpen(false));
  }, [apiUrl]);

  const windowOpen = publicWindowOpen || earlyAccessWindowOpen;
  const needsEarlyAccessChoice = !!session && earlyAccess && !earlyAccessChoice && !windowOpen;

  // ── Claim free early access ─────────────────────────────────────────────────
  const claimEarlyAccess = async () => {
    if (!session) { navigate('/login', { state: { next: '/pricing' } }); return; }
    setClaimingEarlyAccess(true);
    setErrorMsg('');
    try {
      const res  = await fetch(`${apiUrl}/api/billing/claim-early-access`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const json = await res.json();
      if (!res.ok) { setErrorMsg(json?.error ?? 'Could not activate early access. Please try again.'); return; }
      await refreshPlan();
      navigate('/welcome');
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.');
    } finally {
      setClaimingEarlyAccess(false);
    }
  };

  // ── Resolve early-access choice ─────────────────────────────────────────────
  const resolveEarlyAccessChoice = async (choice: 'paid_discount' | 'free') => {
    if (!session) return;
    setChoiceLoading(choice);
    setErrorMsg('');
    try {
      const res  = await fetch(`${apiUrl}/api/billing/early-access-choice`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ choice }),
      });
      const json = await res.json();
      if (!res.ok) { setErrorMsg(json?.error ?? 'Could not save your choice. Please try again.'); return; }
      await refreshPlan();
      if (choice === 'paid_discount') {
        const explorerTier = TIERS.find(t => t.planKey === 'plus');
        if (explorerTier) await handleSubscribe(explorerTier);
      }
    } catch {
      setErrorMsg('Network error. Please check your connection and try again.');
    } finally {
      setChoiceLoading(null);
    }
  };

  // ── Subscribe / checkout ────────────────────────────────────────────────────
  const handleSubscribe = async (tier: Tier) => {
    // Free tier → sign up
    if (tier.planKey === 'free') { navigate('/signup'); return; }

    // Not signed in → login
    if (!session) { navigate('/login', { state: { next: '/pricing' } }); return; }

    const planKey = tier.planKey as CheckoutTierId;

    // Already on this plan → go to dashboard
    if (currentPlan === planKey) { navigate('/app/dashboard'); return; }

    // Early access window still open + Explorer → claim free
    if (planKey === 'plus' && windowOpen && !earlyAccess) {
      await claimEarlyAccess();
      return;
    }

    setCheckoutState(prev => ({ ...prev, [planKey]: 'loading' }));
    setErrorMsg('');

    try {
      const res  = await fetch(`${apiUrl}/api/billing/checkout`, {
        method:  'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body:    JSON.stringify({ plan: planKey }),
      });
      const json = await res.json();

      if (!res.ok) {
        setErrorMsg(json?.error ?? 'Something went wrong. Please try again.');
        setCheckoutState(prev => ({ ...prev, [planKey]: 'error' }));
        return;
      }

      const checkoutUrl: string = json.checkout_url;
      if (!checkoutUrl) {
        setErrorMsg('No checkout URL returned. Please try again.');
        setCheckoutState(prev => ({ ...prev, [planKey]: 'error' }));
        return;
      }

      // Open Polar embedded checkout iframe
      setCheckoutState(prev => ({ ...prev, [planKey]: 'overlay_open' }));

      const checkout = await PolarEmbedCheckout.create(checkoutUrl, {
        theme: 'light',
        onLoaded: () => {
          // Checkout iframe fully loaded
        },
      });

      activeCheckoutRef.current = checkout;

      checkout.addEventListener('success', async () => {
        setCheckoutState({ plus: 'idle', pro: 'idle' });
        activeCheckoutRef.current = null;
        await refreshPlan();
        navigate('/welcome');
      });

      checkout.addEventListener('close', () => {
        setCheckoutState(prev => ({
          ...prev,
          [planKey]: prev[planKey] === 'overlay_open' ? 'idle' : prev[planKey],
        }));
        activeCheckoutRef.current = null;
      });

    } catch (err) {
      console.error('[PricingPage] Checkout error:', err);
      setErrorMsg('Network error. Please check your connection and try again.');
      setCheckoutState(prev => ({ ...prev, [planKey]: 'error' }));
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────────
  const getCtaLabel = (tier: Tier): string => {
    if (tier.planKey === 'free') return 'START EXPLORING';
    const planKey = tier.planKey as CheckoutTierId;
    if (!authLoading && session && currentPlan === planKey) return 'CURRENT PLAN';
    if (planKey === 'plus' && windowOpen && !earlyAccess) {
      return claimingEarlyAccess ? 'Activating…' : 'CLAIM FREE ACCESS';
    }
    const state = checkoutState[planKey];
    if (state === 'loading')      return 'Opening checkout…';
    if (state === 'overlay_open') return 'Complete in overlay…';
    if (tier.highlighted) return 'START FREE TRIAL';
    return 'GET STARTED';
  };

  const getFormattedPrice = (tier: Tier): string => {
    if (tier.planKey === 'free') return '';
    if (pricesLoading) {
      // Fallback for SSR/Prerendering so crawlers see real prices
      return tier.planKey === 'plus' ? '$4.99' : '$7.99';
    }
    const priceData = tier.planKey === 'plus' ? prices.explorer : prices.master;
    return priceData?.display ?? (tier.planKey === 'plus' ? '$4.99' : '$7.99');
  };

  const isCurrent = (tier: Tier) =>
    !!session && tier.planKey !== 'free' && currentPlan === tier.planKey;

  return (
    <>
      {/* ── Header ── */}
      <header className={`pb-12 text-center ${isLandingPage ? 'pt-0' : 'pt-20'}`}>
        <div className="max-w-[720px] mx-auto px-base md:px-gutter space-y-4">
          {isLandingPage ? (
            <h2 className="text-4xl md:text-5xl font-bold text-on-surface" style={ff}>
              How much does the Learnex AI study tool cost?
            </h2>
          ) : (
            <h1 className="text-4xl md:text-5xl font-bold text-on-surface" style={ff}>
              How much does the Learnex AI study tool cost?
            </h1>
          )}
          <p className="text-base text-on-surface-variant max-w-lg mx-auto">
            Choose the path that best supports your academic journey.
          </p>
        </div>
      </header>

      {/* ── Early access window banner ── */}
      {windowOpen && !earlyAccess && (
        <div className="max-w-[1000px] mx-auto px-base md:px-gutter w-full mb-6">
          <div className="p-4 rounded-xl bg-[#D46B4E]/10 border border-[#D46B4E]/30 flex items-start gap-3">
            <span className="material-symbols-outlined text-[#D46B4E] text-[18px] mt-0.5" aria-hidden="true">rocket_launch</span>
            <p className="text-sm text-on-surface">
              <strong>Launch Special!</strong> For the first 15 days, sign up to get our Explorer plan completely free. Plus, if you upgrade to any paid plan during this period, you'll lock in a massive 50% discount for life!
            </p>
          </div>
        </div>
      )}

      {/* ── Resolve early access (billing live, choice not made yet) ── */}
      {needsEarlyAccessChoice && (
        <div className="max-w-[1000px] mx-auto px-base md:px-gutter w-full mb-6">
          <div className="p-5 rounded-xl bg-[#286958]/5 border border-[#286958]/25 flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <div>
              <p className="text-sm font-semibold text-on-surface">Payments are live — keep your Explorer access?</p>
              <p className="text-sm text-on-surface-variant mt-1">
                As an early access member you get {earlyAccessDiscountPercent ?? 50}% off Explorer for life,
                or you can switch to the free Hiker plan instead.
              </p>
            </div>
            <div className="flex gap-3 shrink-0">
              <button
                onClick={() => resolveEarlyAccessChoice('free')}
                disabled={choiceLoading !== null}
                className="px-4 py-2 rounded-full text-sm font-semibold border border-outline-variant/40 text-on-surface-variant hover:border-outline-variant/70 disabled:opacity-50"
              >
                {choiceLoading === 'free' ? 'Switching…' : 'Switch to free'}
              </button>
              <button
                onClick={() => resolveEarlyAccessChoice('paid_discount')}
                disabled={choiceLoading !== null}
                className="px-4 py-2 rounded-full text-sm font-semibold bg-[#286958] text-white disabled:opacity-50"
              >
                {choiceLoading === 'paid_discount'
                  ? 'Opening checkout…'
                  : `Continue at ${earlyAccessDiscountPercent ?? 50}% off`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Error banner ── */}
      {errorMsg && (
        <div className="max-w-[1000px] mx-auto px-base md:px-gutter w-full mb-4">
          <div className="p-4 rounded-xl bg-error/5 border border-error/20 flex items-start gap-3">
            <span className="material-symbols-outlined text-error text-[18px] mt-0.5" aria-hidden="true">error</span>
            <p className="text-sm text-error">{errorMsg}</p>
          </div>
        </div>
      )}

      {/* ── Plan cards ── */}
      <section aria-label="Pricing plans" className="pb-20">
        <div className="max-w-[1000px] mx-auto px-base md:px-gutter grid md:grid-cols-3 gap-6 items-stretch">
          {TIERS.map((tier) => {
            const label      = getCtaLabel(tier);
            const isCur      = isCurrent(tier);
            const planKey    = tier.planKey as CheckoutTierId;
            const state      = tier.planKey !== 'free' ? checkoutState[planKey] : 'idle';
            const isDisabled = isCur || state === 'loading' || state === 'overlay_open' || claimingEarlyAccess;
            const price      = getFormattedPrice(tier);

            return (
              <div
                key={tier.planKey}
                className={`relative bg-surface-white rounded-2xl p-8 flex flex-col transition-all ${
                  tier.highlighted
                    ? 'border-2 border-primary-container shadow-lg'
                    : 'border border-outline-variant/20 shadow-sm'
                }`}
              >
                {/* Badge */}
                {tier.badge && (
                  <div
                    className="absolute -top-4 left-1/2 -translate-x-1/2 bg-primary-container text-white text-[10px] font-bold px-4 py-1.5 rounded-full tracking-widest whitespace-nowrap"
                    style={mono}
                  >
                    {tier.badge}
                  </div>
                )}

                {/* Icon + Name */}
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${tier.highlighted ? 'bg-primary-container/10' : 'bg-surface-container-low'}`}>
                    <span className={`material-symbols-outlined text-[20px] ${tier.highlighted ? 'text-primary-container' : 'text-on-surface-variant'}`} aria-hidden="true">
                      {tier.icon}
                    </span>
                  </div>
                  <h2 className="text-xl font-semibold text-on-surface" style={ff}>{tier.name}</h2>
                </div>

                {/* Price */}
                <div className="flex items-baseline gap-1 mb-3 min-h-[3.5rem]">
                  {tier.planKey === 'free' ? (
                    <span className="text-5xl font-bold text-on-surface" style={ff}>Free</span>
                  ) : price ? (
                    <>
                      <span className="text-4xl font-bold text-on-surface" style={ff}>{price}</span>
                      <span className="text-sm text-on-surface-variant ml-1">/ month</span>
                    </>
                  ) : (
                    <span className="text-lg font-semibold text-on-surface-variant" style={ff}>See checkout</span>
                  )}
                </div>

                {/* Blurb */}
                <p className="text-sm text-on-surface-variant mb-6 leading-relaxed min-h-[2.5rem]">{tier.blurb}</p>

                {/* Features */}
                <ul className="space-y-3 mb-8 flex-1">
                  {tier.features.map((f) => (
                    <li key={f} className="flex items-center gap-2 text-sm text-on-surface">
                      <span className="material-symbols-outlined text-[18px] text-primary-container" aria-hidden="true">check_circle</span>
                      {f}
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <button
                  onClick={() => handleSubscribe(tier)}
                  disabled={isDisabled}
                  className={`block w-full text-center py-3 rounded-xl text-sm font-semibold tracking-widest transition-all disabled:opacity-60 disabled:cursor-default flex items-center justify-center gap-2 ${
                    isCur
                      ? 'bg-secondary-container text-on-secondary-container'
                      : tier.highlighted
                      ? 'bg-primary-container text-white hover:opacity-90 shadow-sm hover:shadow-md'
                      : 'border border-outline-variant/40 text-on-surface hover:bg-surface-container-low'
                  }`}
                  style={mono}
                >
                  {state === 'loading' && (
                    <div className="w-4 h-4 rounded-full border-2 border-current/30 border-t-current animate-spin" />
                  )}
                  {label}
                </button>
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Compare the Paths ── */}
      {showCompare && (
        <section aria-labelledby="compare-heading" className="pb-28">
          <div className="max-w-[900px] mx-auto px-base md:px-gutter">
            <h2 id="compare-heading" className="text-3xl font-semibold text-on-surface text-center mb-10" style={ff}>
              Compare the Paths
            </h2>
            <div className="bg-surface-white rounded-2xl border border-outline-variant/10 shadow-sm overflow-hidden">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-outline-variant/15">
                    <th className="py-4 px-6 text-xs font-bold text-on-surface-variant tracking-widest" style={mono}>FEATURE</th>
                    <th className="py-4 px-4 text-sm font-semibold text-on-surface text-center">Hiker</th>
                    <th className="py-4 px-4 text-sm font-semibold text-primary-container text-center bg-primary-container/5">Explorer</th>
                    <th className="py-4 px-4 text-sm font-semibold text-on-surface text-center">Master</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row) => (
                    <tr key={row.feature} className="border-b border-outline-variant/10 last:border-0">
                      <td className="py-4 px-6 text-sm text-on-surface">{row.feature}</td>
                      <td className="py-4 px-4 text-sm text-on-surface-variant text-center"><CellValue value={row.free} /></td>
                      <td className="py-4 px-4 text-sm text-center bg-primary-container/5"><CellValue value={row.plus} isExplorer /></td>
                      <td className="py-4 px-4 text-sm text-on-surface-variant text-center"><CellValue value={row.pro} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}
    </>
  );
}

export default function PricingPage() {
  usePageMeta(
    'Learnex Pricing — Free, Explorer & Master Plans',
    'Simple pricing for students: unlimited quizzes and flashcards free forever, plus affordable AI video lessons. Explorer $4.99/mo · Master $7.99/mo.',
    '/pricing'
  );

  useSchema({
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Product',
        name: 'Learnex Explorer',
        description: 'Unlimited subjects, full AI video lessons, and priority generation for students.',
        image: 'https://learnex.cc/logo/preview-sheet.png',
        sku: 'learnex-explorer',
        brand: {
          '@type': 'Brand',
          name: 'Learnex',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '89',
        },
        review: {
          '@type': 'Review',
          author: { '@type': 'Person', name: 'Alex' },
          datePublished: '2024-05-12',
          reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
          reviewBody: 'This study tool has completely changed how I learn!',
        },
        offers: {
          '@type': 'Offer',
          price: '4.99',
          priceCurrency: 'USD',
          validFrom: '2024-01-01T00:00:00Z',
          priceValidUntil: '2027-12-31',
          availability: 'https://schema.org/InStock',
          url: 'https://learnex.cc/pricing',
          hasMerchantReturnPolicy: {
            '@type': 'MerchantReturnPolicy',
            applicableCountry: 'US',
            returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
          },
          shippingDetails: {
            '@type': 'OfferShippingDetails',
            shippingRate: {
              '@type': 'MonetaryAmount',
              value: 0,
              currency: 'USD',
            },
            shippingDestination: {
              '@type': 'DefinedRegion',
              addressCountry: 'US',
            },
            deliveryTime: {
              '@type': 'ShippingDeliveryTime',
              handlingTime: {
                '@type': 'QuantitativeValue',
                minValue: 0,
                maxValue: 0,
                unitCode: 'DAY',
              },
              transitTime: {
                '@type': 'QuantitativeValue',
                minValue: 0,
                maxValue: 0,
                unitCode: 'DAY',
              },
            },
          },
        },
      },
      {
        '@type': 'Product',
        name: 'Learnex Master',
        description: 'Everything in Explorer plus priority generation speed.',
        image: 'https://learnex.cc/logo/preview-sheet.png',
        sku: 'learnex-master',
        brand: {
          '@type': 'Brand',
          name: 'Learnex',
        },
        aggregateRating: {
          '@type': 'AggregateRating',
          ratingValue: '4.9',
          reviewCount: '89',
        },
        review: {
          '@type': 'Review',
          author: { '@type': 'Person', name: 'Alex' },
          datePublished: '2024-05-12',
          reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
          reviewBody: 'This study tool has completely changed how I learn!',
        },
        offers: {
          '@type': 'Offer',
          price: '7.99',
          priceCurrency: 'USD',
          validFrom: '2024-01-01T00:00:00Z',
          priceValidUntil: '2027-12-31',
          availability: 'https://schema.org/InStock',
          url: 'https://learnex.cc/pricing',
          hasMerchantReturnPolicy: {
            '@type': 'MerchantReturnPolicy',
            applicableCountry: 'US',
            returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
          },
          shippingDetails: {
            '@type': 'OfferShippingDetails',
            shippingRate: {
              '@type': 'MonetaryAmount',
              value: 0,
              currency: 'USD',
            },
            shippingDestination: {
              '@type': 'DefinedRegion',
              addressCountry: 'US',
            },
            deliveryTime: {
              '@type': 'ShippingDeliveryTime',
              handlingTime: {
                '@type': 'QuantitativeValue',
                minValue: 0,
                maxValue: 0,
                unitCode: 'DAY',
              },
              transitTime: {
                '@type': 'QuantitativeValue',
                minValue: 0,
                maxValue: 0,
                unitCode: 'DAY',
              },
            },
          },
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://learnex.cc/' },
          { '@type': 'ListItem', position: 2, name: 'Pricing', item: 'https://learnex.cc/pricing' },
        ],
      },
    ],
  });

  return (
    <div className="bg-paper-bg text-on-surface antialiased min-h-screen flex flex-col">
      <PublicNav activePath="/pricing" />
      <PricingContent showCompare={true} />
      <PublicFooter />
    </div>
  );
}